import { clamp, hexToRgb, luminance, mixRgb, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// Colours at the foot of the mountain in the blue hour before dawn, at first
// light halfway up, and at sunrise on the summit. The three keys sit at evenly
// spaced lightness in a deliberately narrow range (a blue pre-dawn, never
// black, to a warm, unblown sunrise), so every step of the climb brightens the
// scene by about the same, gentle amount.
const STOPS = {
  skyTop: ['#2A3159', '#323B79', '#2B4D90'],
  skyMiddle: ['#3D4882', '#535595', '#7C70AC'],
  skyHorizon: ['#56619F', '#B07890', '#E49B76'],
  farRange: ['#47528A', '#726797', '#967AA1'],
  midRange: ['#404978', '#615686', '#775C8B'],
  nearRange: ['#383F68', '#4E467A', '#534174'],
  rockLit: ['#444E80', '#5D5080', '#7A4C56'],
  rockShadow: ['#31375C', '#323562', '#302D5F'],
  snowLit: ['#B0C0E6', '#CFAEC2', '#E2B392'],
  snowShadow: ['#6E7BB0', '#7A7CB4', '#777EB9'],
  rim: ['#97A6DA', '#E4A0B0', '#E8A560'],
  foreground: ['#2A2F4F', '#28294B', '#262444'],
} as const;

type ColorName = keyof typeof STOPS;

export type MountainColors = Record<ColorName, Rgb>;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = NAMES.reduce(
  (all, name) => ({ ...all, [name]: STOPS[name].map(hexToRgb) }),
  {} as Record<ColorName, Rgb[]>,
);

/**
 * Which two key lights an altitude falls between, and how far from the first.
 * Linear, so equal steps of the climb are equal steps of light (no plateaus at the keys).
 */
export const mountainKey = (altitude: number) => {
  const scaled = clamp(altitude) * 2;
  const index = Math.min(1, Math.floor(scaled));
  return { index, amount: clamp(scaled - index) };
};

/** The light on the mountain at an altitude (0 at the foot before dawn, 1 at the summit at sunrise). */
export const mountainColors = (altitude: number): MountainColors => {
  const { index, amount } = mountainKey(altitude);
  return NAMES.reduce(
    (all, name) => ({ ...all, [name]: mixRgb(PARSED[name][index], PARSED[name][index + 1], amount) }),
    {} as MountainColors,
  );
};

// Above this sky luminance dark header text contrasts better than white (both
// are about 4.2:1 here). The sky top peaks near 0.08 at sunrise, and the scene
// keeps the sun's glow out of the header band, so the header stays white.
const DARK_CHROME_LUMINANCE = 0.2;

/** The sky stays deep enough at the top for white header text from night to sunrise. */
export const mountainChromeTone = (progress: number): ChromeTone =>
  luminance(mountainColors(progress).skyTop) > DARK_CHROME_LUMINANCE ? 'dark' : 'light';
